import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Grid, FormControlLabel, Radio, RadioGroup,
  MenuItem, Alert, CircularProgress, Chip, Card, CardActionArea, CardContent, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import { Save as SaveIcon, PlayArrow as StartIcon, Cancel as CancelIcon, Search as SearchIcon, Print as PrintIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getCustomers, getActiveBranches, getDeviceTypes, createServiceRequest,
  getProducts, getProductSerials, createProduct, getWarrantyInfo,
  getDeviceBrands, createDeviceBrand
} from '../../services/api';
import { printRepairGrn } from '../../utils/repairGrnPrint';
import { SSI_DEVICE_CATEGORIES } from '../../constants/productCategories';
import { canAccessAllBranches, getCurrentBranchLabel } from '../../utils/auth';

const DEVICE_BRANDS = ['Apple', 'Samsung', 'Huawei', 'Xiaomi', 'Dell', 'HP', 'Lenovo', 'Asus', 'Other'];
const ISSUE_CATEGORIES = ['Hardware', 'Software', 'Screen', 'Battery', 'Water Damage', 'Data Recovery', 'Other'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const ADD_NEW_MODEL = '__ADD_NEW_MODEL__';
const ADD_NEW_BRAND = '__ADD_NEW_BRAND__';
const SSI_CATEGORIES = SSI_DEVICE_CATEGORIES;
const ServiceRequestForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [deviceBrands, setDeviceBrands] = useState(DEVICE_BRANDS);
  const [customerMode, setCustomerMode] = useState('existing');
  const [attachments, setAttachments] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [loadingSerials, setLoadingSerials] = useState(false);

  const [form, setForm] = useState({
    customer_id: '', branch_id: '',
    customer_phone: '', customer_email: '',
    device_type: 'Mobile Phone', device_brand: 'Apple', device_model: '',
    device_serial: '', device_imei: '', purchase_date: '',
    issue_category: 'Hardware', priority: 'high',
    device_issue: '', physical_condition: '', accessories_received: '', notes: '',
    service_coverage: 'billable',
    warranty_action: 'repair',
    exchange_channel: 'ssi_office',
    exchange_product_id: null,
    exchange_serial_id: null,
    exchange_serial_number: '',
    exchange_product_name: '',
    exchange_product_model: '',
    exchange_category: '',
    incoming_serial_number: '',
    incoming_category: 'Laptop Computers',
    incoming_model: '',
    incoming_product_id: null,
    exchange_outsource_notes: '',
    original_invoice_number: '',
    original_pos_id: null,
    original_invoice_items: []
  });

  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [addingModel, setAddingModel] = useState(false);
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [addingBrand, setAddingBrand] = useState(false);
  const [invoiceLookup, setInvoiceLookup] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [oldInvoice, setOldInvoice] = useState(null);
  const [exchangePrintOpen, setExchangePrintOpen] = useState(false);
  const [exchangeSnapshot, setExchangeSnapshot] = useState(null);

  useEffect(() => {
    const myBranchId = localStorage.getItem('erp_branch_id') || '';
    if (!canAccessAllBranches() && myBranchId) {
      setForm((prev) => ({ ...prev, branch_id: myBranchId }));
    }
    Promise.all([
      getCustomers().then(r => setCustomers(r.data || [])),
      getActiveBranches().then(r => setBranches(r.data || [])),
      getDeviceTypes().then(r => setDeviceTypes(r.data || ['Mobile Phone', 'Laptop', 'Tablet'])),
      getDeviceBrands().then(r => {
        const list = r.data || [];
        setDeviceBrands(list.length ? list : DEVICE_BRANDS);
      }).catch(() => setDeviceBrands(DEVICE_BRANDS)),
      getProducts().then(r => setProducts(r.data || []))
    ]).catch(() => {});
  }, []);

  // Keep incoming serial/model in sync with device fields for exchange
  useEffect(() => {
    if (form.service_coverage === 'warranty' && form.warranty_action === 'exchange') {
      setForm((prev) => ({
        ...prev,
        incoming_serial_number: prev.incoming_serial_number || prev.device_serial,
        incoming_model: prev.incoming_model || prev.device_model
      }));
    }
  }, [form.service_coverage, form.warranty_action, form.device_serial, form.device_model]);

  const filteredInventory = useMemo(() => {
    const term = inventorySearch.trim().toLowerCase();
    return (products || []).filter((p) => {
      if (p.is_active === false) return false;
      const good = parseInt(p.good_quantity ?? p.quantity, 10) || 0;
      if (good <= 0) return false;
      if (!term) return true;
      const serialHit = Array.isArray(p.serials) && p.serials.some((s) =>
        (s.serial_number || '').toLowerCase().includes(term) && s.status === 'available'
      );
      return (
        (p.name || '').toLowerCase().includes(term) ||
        (p.product_code || '').toLowerCase().includes(term) ||
        (p.barcode || '').toLowerCase().includes(term) ||
        (p.category || '').toLowerCase().includes(term) ||
        serialHit
      );
    });
  }, [products, inventorySearch]);

  // Product names (models) for the selected incoming warranty category
  const modelsInCategory = useMemo(() => {
    return (products || [])
      .filter((p) => p.is_active !== false && p.category === form.incoming_category)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [products, form.incoming_category]);

  const reloadProducts = async () => {
    const r = await getProducts();
    setProducts(r.data || []);
    return r.data || [];
  };

  const handleIncomingCategoryChange = (category) => {
    setForm({
      ...form,
      incoming_category: category,
      incoming_model: '',
      incoming_product_id: null,
      device_model: ''
    });
  };

  const handleIncomingModelChange = (value) => {
    if (value === ADD_NEW_MODEL) {
      setNewModelName('');
      setAddModelOpen(true);
      return;
    }
    const product = modelsInCategory.find((p) => String(p.id) === String(value));
    setForm({
      ...form,
      incoming_product_id: product ? product.id : null,
      incoming_model: product ? product.name : '',
      device_model: product ? product.name : ''
    });
  };

  const handleCreateNewModel = async () => {
    const name = newModelName.trim();
    if (!name) return;
    try {
      setAddingModel(true);
      setError(null);
      const code = `MDL-${name.replace(/[^A-Za-z0-9]/g, '').slice(0, 12).toUpperCase() || 'NEW'}-${Date.now().toString().slice(-4)}`;
      const res = await createProduct({
        name,
        product_code: code,
        category: form.incoming_category,
        quantity: 0,
        good_quantity: 0,
        issue_quantity: 0,
        track_serials: true,
        is_active: true,
        price: 0,
        cost_price: 0,
        description: 'Created from Repair warranty exchange (incoming model)'
      });
      const created = res.data || res;
      const list = await reloadProducts();
      const match = list.find((p) => p.id === created.id) || created;
      setForm((prev) => ({
        ...prev,
        incoming_product_id: match.id,
        incoming_model: match.name,
        device_model: match.name
      }));
      setAddModelOpen(false);
      setNewModelName('');
    } catch (err) {
      setError(err.message || 'Failed to add new model');
    } finally {
      setAddingModel(false);
    }
  };

  const handleBrandChange = (value) => {
    if (value === ADD_NEW_BRAND) {
      setNewBrandName('');
      setAddBrandOpen(true);
      return;
    }
    setForm({ ...form, device_brand: value });
  };

  const handleCreateNewBrand = async () => {
    const name = newBrandName.trim();
    if (!name) return;
    try {
      setAddingBrand(true);
      setError(null);
      let savedName = name;
      try {
        const res = await createDeviceBrand({ name });
        savedName = res.data?.name || name;
        const list = res.data?.brands;
        if (Array.isArray(list) && list.length) {
          setDeviceBrands(list);
        } else {
          setDeviceBrands((prev) => (prev.includes(savedName) ? prev : [...prev, savedName]));
        }
      } catch (_) {
        setDeviceBrands((prev) => (prev.includes(name) ? prev : [...prev, name]));
      }
      setForm((prev) => ({ ...prev, device_brand: savedName }));
      setAddBrandOpen(false);
      setNewBrandName('');
    } catch (err) {
      setError(err.message || 'Failed to add new brand');
    } finally {
      setAddingBrand(false);
    }
  };

  const handleFileUpload = (e) => {
    Array.from(e.target.files || []).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments(prev => [...prev, {
          file_name: file.name,
          file_type: file.type,
          file_url: reader.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const pickProduct = async (product) => {
    setSelectedProduct(product);
    setLoadingSerials(true);
    try {
      const res = await getProductSerials(product.id, { status: 'available' });
      setAvailableSerials(res.data || []);
    } catch {
      setAvailableSerials([]);
    } finally {
      setLoadingSerials(false);
    }
  };

  const pickSerial = (serial) => {
    setForm({
      ...form,
      exchange_product_id: selectedProduct.id,
      exchange_serial_id: serial.id,
      exchange_serial_number: serial.serial_number,
      exchange_product_name: selectedProduct.name,
      exchange_product_model: selectedProduct.name,
      exchange_category: selectedProduct.category
    });
  };

  const handleLookupOldInvoice = async () => {
    const q = invoiceLookup.trim();
    if (!q) {
      setError('Enter an invoice number or serial to look up');
      return;
    }
    try {
      setLookupLoading(true);
      setError(null);
      const res = await getWarrantyInfo(`?q=${encodeURIComponent(q)}`);
      const payload = res.data || res;
      const tx = payload.transaction || payload;
      const items = payload.items || tx.items || [];
      if (!tx || (!tx.id && !tx.pos_number)) {
        setError('No POS invoice found');
        setOldInvoice(null);
        return;
      }
      setOldInvoice({ transaction: tx, items, customer: payload.customer });
      setForm((prev) => ({
        ...prev,
        original_invoice_number: tx.pos_number || tx.transaction_number || q,
        original_pos_id: tx.id || null,
        original_invoice_items: items,
        device_serial: prev.device_serial || (
          Array.isArray(items[0]?.serial_numbers) ? items[0].serial_numbers[0] : ''
        ) || prev.device_serial,
        incoming_serial_number: prev.incoming_serial_number || (
          Array.isArray(items[0]?.serial_numbers) ? items[0].serial_numbers[0] : ''
        ) || prev.incoming_serial_number
      }));
      if (payload.customer?.id && customerMode === 'existing') {
        setForm((prev) => ({
          ...prev,
          customer_id: payload.customer.id,
          customer_phone: payload.customer.phone || prev.customer_phone,
          customer_email: payload.customer.email || prev.customer_email
        }));
      }
    } catch (err) {
      setOldInvoice(null);
      const msg = err.message || err.response?.data?.message || '';
      if (err.status === 404 || /not found/i.test(msg)) {
        setError(msg || 'No POS invoice found. Try full number like INV-20260814-006 or the item serial.');
      } else {
        setError(msg || 'Failed to look up old invoice');
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (startRepair = false) => {
    try {
      setLoading(true);
      setError(null);

      if (form.service_coverage === 'warranty' && !form.warranty_action) {
        setError('Please select Exchange item or Repair item for warranty');
        setLoading(false);
        return;
      }

      if (form.service_coverage === 'warranty' && form.warranty_action === 'exchange') {
        if (!form.exchange_channel) {
          setError('Select SSI Office or Outsource for exchange');
          setLoading(false);
          return;
        }
        if (form.exchange_channel === 'ssi_office') {
          if (!form.exchange_serial_id) {
            setError('Search and select an exchange item (serial) from SSI inventory');
            setLoading(false);
            return;
          }
          if (!form.incoming_serial_number || !form.incoming_category) {
            setError('Enter incoming warranty serial number and category for SSI Technical');
            setLoading(false);
            return;
          }
          if (!form.incoming_model && !form.incoming_product_id) {
            setError('Select a model (product name) for the incoming warranty device');
            setLoading(false);
            return;
          }
        }
      }

      const payload = {
        ...form,
        branch_id: form.branch_id ? parseInt(form.branch_id) : null,
        warranty_action: form.service_coverage === 'warranty' ? form.warranty_action : null,
        exchange_channel:
          form.service_coverage === 'warranty' && form.warranty_action === 'exchange'
            ? form.exchange_channel
            : null,
        original_invoice_number: form.original_invoice_number || null,
        original_pos_id: form.original_pos_id || null,
        original_invoice_items: form.original_invoice_items || [],
        start_repair: startRepair,
        send_sms: false,
        attachments,
        new_customer: customerMode === 'new' ? newCustomer : null,
        customer_id: customerMode === 'existing' ? parseInt(form.customer_id) : undefined,
        auto_assign: startRepair
      };
      const res = await createServiceRequest(payload);
      if (res.success) {
        printRepairGrn(res.data);
        const ssiId = res.data.ssi_technical_job_id;
        const nextPath = (res.data.redirect_to_ssi && ssiId)
          ? `/ssi-technical/${ssiId}`
          : (res.data.id ? `/repairs/${res.data.id}` : null);
        if (res.data.exchange_invoice_snapshot) {
          setExchangeSnapshot({
            ...res.data.exchange_invoice_snapshot,
            _navigate: nextPath
          });
          setExchangePrintOpen(true);
        } else if (nextPath) {
          navigate(nextPath);
        } else {
          setError('Created, but no job ID returned');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to create service request');
    } finally {
      setLoading(false);
    }
  };

  const onCustomerSelect = (id) => {
    const c = customers.find(x => String(x.id) === String(id));
    setForm({ ...form, customer_id: id, customer_phone: c?.phone || '', customer_email: c?.email || '' });
  };

  const isExchange = form.service_coverage === 'warranty' && form.warranty_action === 'exchange';
  const isSsiOffice = isExchange && form.exchange_channel === 'ssi_office';

  return (
    <Box maxWidth="1000px" mx="auto">
      <Typography variant="h4" fontWeight={600} gutterBottom>Create Service Request</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Customer Information</Typography>
        <RadioGroup row value={customerMode} onChange={(e) => setCustomerMode(e.target.value)} sx={{ mb: 2 }}>
          <FormControlLabel value="existing" control={<Radio />} label="Existing Customer" />
          <FormControlLabel value="new" control={<Radio />} label="New Customer" />
        </RadioGroup>
        {customerMode === 'existing' ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth select label="Customer" value={form.customer_id} onChange={(e) => onCustomerSelect(e.target.value)}>
                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name} - {c.phone}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth label="Phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Name" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Phone" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} /></Grid>
          </Grid>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Service Coverage</Typography>
        <RadioGroup
          row
          value={form.service_coverage}
          onChange={(e) => setForm({
            ...form,
            service_coverage: e.target.value,
            warranty_action: e.target.value === 'warranty' ? (form.warranty_action || 'repair') : null
          })}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="billable" control={<Radio />} label="Billable" />
          <FormControlLabel value="warranty" control={<Radio />} label="Warranty" />
        </RadioGroup>

        {form.service_coverage === 'warranty' && (
          <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'success.main', mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Warranty action</Typography>
            <RadioGroup
              row
              value={form.warranty_action || 'repair'}
              onChange={(e) => setForm({ ...form, warranty_action: e.target.value })}
            >
              <FormControlLabel value="repair" control={<Radio />} label="Repair item (everything free)" />
              <FormControlLabel value="exchange" control={<Radio />} label="Exchange item" />
            </RadioGroup>
          </Box>
        )}

        {isExchange && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Exchange channel</Typography>
            <RadioGroup
              row
              value={form.exchange_channel}
              onChange={(e) => setForm({ ...form, exchange_channel: e.target.value })}
            >
              <FormControlLabel value="ssi_office" control={<Radio />} label="SSI Office" />
              <FormControlLabel value="outsource" control={<Radio />} label="Outsource" />
            </RadioGroup>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Lookup old invoice (invoice # or serial)
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. INV-20260814-006 or serial number"
                    value={invoiceLookup}
                    onChange={(e) => setInvoiceLookup(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookupOldInvoice(); } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={lookupLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                    disabled={lookupLoading}
                    onClick={handleLookupOldInvoice}
                  >
                    Lookup
                  </Button>
                </Grid>
              </Grid>
              {oldInvoice?.transaction && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Old invoice:</strong> {oldInvoice.transaction.pos_number || oldInvoice.transaction.transaction_number}
                  {' · '}
                  {oldInvoice.customer?.name || oldInvoice.transaction.customer_name || '-'}
                  {' · '}
                  {oldInvoice.transaction.transaction_date
                    ? new Date(oldInvoice.transaction.transaction_date).toLocaleDateString()
                    : ''}
                  <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                    {(oldInvoice.items || []).map((it, i) => (
                      <li key={i}>
                        {it.product_name}
                        {Array.isArray(it.serial_numbers) && it.serial_numbers.length
                          ? ` — SN ${it.serial_numbers.join(', ')}`
                          : ''}
                        {it.warranty_months ? ` (${it.warranty_months} mo warranty)` : ''}
                      </li>
                    ))}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/pos/sales?search=${encodeURIComponent(oldInvoice.transaction.pos_number || invoiceLookup)}`)}
                  >
                    Open in POS Sales
                  </Button>
                </Alert>
              )}
              {form.original_invoice_number && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Linked original invoice: {form.original_invoice_number}
                  {form.original_pos_id ? ` (POS #${form.original_pos_id})` : ''}
                </Typography>
              )}
            </Box>

            {form.exchange_channel === 'outsource' && (
              <TextField
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 2 }}
                label="Outsource notes"
                value={form.exchange_outsource_notes}
                onChange={(e) => setForm({ ...form, exchange_outsource_notes: e.target.value })}
              />
            )}
          </Box>
        )}
      </Paper>

      {isSsiOffice && (
        <>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Exchange item from SSI inventory</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search like POS — pick product, then select serial. This unit is given to the customer and deducted from stock.
            </Typography>
            <TextField
              fullWidth
              placeholder="Scan barcode, serial number, or search products..."
              value={inventorySearch}
              onChange={(e) => setInventorySearch(e.target.value)}
              sx={{ mb: 2 }}
            />
            {form.exchange_serial_number && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Selected exchange: <strong>{form.exchange_product_name}</strong> — SN <strong>{form.exchange_serial_number}</strong>
                {form.exchange_category ? ` (${form.exchange_category})` : ''}
              </Alert>
            )}
            <Grid container spacing={1} sx={{ maxHeight: 280, overflow: 'auto' }}>
              {filteredInventory.slice(0, 40).map((p) => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <Card variant={selectedProduct?.id === p.id ? 'outlined' : 'elevation'} sx={{ borderColor: selectedProduct?.id === p.id ? 'primary.main' : undefined }}>
                    <CardActionArea onClick={() => pickProduct(p)}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle2" noWrap>{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.product_code} · {p.category}</Typography>
                        <Typography variant="body2">Stock: {p.good_quantity ?? p.quantity}</Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {selectedProduct && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2" gutterBottom>Available serials — {selectedProduct.name}</Typography>
                {loadingSerials ? <CircularProgress size={22} /> : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {availableSerials.length === 0 && <Typography variant="body2" color="text.secondary">No available serials</Typography>}
                    {availableSerials.map((s) => (
                      <Chip
                        key={s.id}
                        label={s.serial_number}
                        color={form.exchange_serial_id === s.id ? 'primary' : 'default'}
                        onClick={() => pickSerial(s)}
                        clickable
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Incoming warranty device → SSI Technical</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Category uses the same names as the Products page. Model lists product names in that category.
              After SSI Technical approval, stock qty increases on the selected model.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth select label="Item category"
                  value={form.incoming_category}
                  onChange={(e) => handleIncomingCategoryChange(e.target.value)}
                >
                  {SSI_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth select label="Model (product name)"
                  value={form.incoming_product_id || ''}
                  onChange={(e) => handleIncomingModelChange(e.target.value)}
                  helperText={modelsInCategory.length ? `${modelsInCategory.length} models in this category` : 'No products yet — add a new model'}
                >
                  <MenuItem value=""><em>Select model</em></MenuItem>
                  {modelsInCategory.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                  <MenuItem value={ADD_NEW_MODEL}>➕ Add new model…</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Incoming serial number"
                  value={form.incoming_serial_number}
                  onChange={(e) => setForm({ ...form, incoming_serial_number: e.target.value, device_serial: e.target.value })}
                />
              </Grid>
            </Grid>
            {form.incoming_model && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Selected model: <strong>{form.incoming_model}</strong>
                {form.incoming_product_id ? ` (product #${form.incoming_product_id})` : ''} — stock will increase here after admin approve.
              </Alert>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              After save, opens <strong>SSI Technical</strong> under category <strong>{form.incoming_category}</strong>.
            </Alert>
          </Paper>
        </>
      )}

      <Dialog open={addModelOpen} onClose={() => setAddModelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add new model — {form.incoming_category}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a product in the Products catalog under this category (same names as Products page).
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label="Model / product name"
            value={newModelName}
            onChange={(e) => setNewModelName(e.target.value)}
            placeholder="e.g. Dell Latitude 5420"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModelOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={addingModel || !newModelName.trim()} onClick={handleCreateNewModel}>
            {addingModel ? <CircularProgress size={20} /> : 'Add model'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addBrandOpen} onClose={() => setAddBrandOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add new brand</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            label="Brand name"
            value={newBrandName}
            onChange={(e) => setNewBrandName(e.target.value)}
            placeholder="e.g. Apple, Samsung, Dell"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateNewBrand();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBrandOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={addingBrand || !newBrandName.trim()} onClick={handleCreateNewBrand}>
            {addingBrand ? <CircularProgress size={20} /> : 'Add brand'}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Device Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <TextField fullWidth select label="Type" value={form.device_type} onChange={(e) => setForm({ ...form, device_type: e.target.value })}>
              {deviceTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              select
              label="Brand"
              value={form.device_brand}
              onChange={(e) => handleBrandChange(e.target.value)}
            >
              {Array.from(new Set([...(deviceBrands || []), form.device_brand].filter(Boolean))).map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
              <MenuItem value={ADD_NEW_BRAND} sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                + Add new brand
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}><TextField fullWidth label="Model" value={form.device_model} onChange={(e) => setForm({ ...form, device_model: e.target.value, incoming_model: isSsiOffice ? e.target.value : form.incoming_model })} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth label="Serial" value={form.device_serial} onChange={(e) => setForm({ ...form, device_serial: e.target.value, incoming_serial_number: isSsiOffice ? e.target.value : form.incoming_serial_number })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="IMEI" value={form.device_imei} onChange={(e) => setForm({ ...form, device_imei: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth type="date" label="Purchase Date" InputLabelProps={{ shrink: true }} value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></Grid>
          <Grid item xs={12}>
            {canAccessAllBranches() ? (
              <TextField fullWidth select label="Branch" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                <MenuItem value="">Select Branch</MenuItem>
                {branches.map(b => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}{b.branch_code ? ` (${b.branch_code})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                label="Branch"
                value={getCurrentBranchLabel() || 'Your branch'}
                InputProps={{ readOnly: true }}
              />
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Issue Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth select label="Category" value={form.issue_category} onChange={(e) => setForm({ ...form, issue_category: e.target.value })}>
              {ISSUE_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map(p => <MenuItem key={p} value={p}>{p.toUpperCase()}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Description" value={form.device_issue} onChange={(e) => setForm({ ...form, device_issue: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Physical Condition" value={form.physical_condition} onChange={(e) => setForm({ ...form, physical_condition: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Accessories Received" value={form.accessories_received} onChange={(e) => setForm({ ...form, accessories_received: e.target.value })} /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, border: '2px dashed #ccc', textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Attachments</Typography>
        <Button variant="outlined" component="label">
          Upload Photos
          <input type="file" hidden multiple accept="image/*" onChange={handleFileUpload} />
        </Button>
        <Box sx={{ mt: 1 }}>{attachments.map((a, i) => <Chip key={i} label={a.file_name} sx={{ m: 0.5 }} />)}</Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button startIcon={<CancelIcon />} onClick={() => navigate('/repairs')}>Cancel</Button>
        <Button variant="outlined" startIcon={<SaveIcon />} disabled={loading} onClick={() => handleSubmit(false)}>
          {loading ? <CircularProgress size={20} /> : (isSsiOffice ? 'Save & Open SSI Technical' : 'Save')}
        </Button>
        <Button variant="contained" startIcon={<StartIcon />} disabled={loading} onClick={() => handleSubmit(true)}>
          Save & Start Repair
        </Button>
      </Box>

      <Dialog
        open={exchangePrintOpen}
        onClose={() => {
          const next = exchangeSnapshot?._navigate;
          setExchangePrintOpen(false);
          if (next) navigate(next);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Warranty Exchange Invoice</DialogTitle>
        <DialogContent>
          {exchangeSnapshot && (
            <Box id="exchange-invoice-print" sx={{ p: 1 }}>
              <Typography variant="h6" gutterBottom>Exchange Receipt</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Service: {exchangeSnapshot.service_number} · JOB: {exchangeSnapshot.grn_number}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Customer: {exchangeSnapshot.customer?.name || '-'}
                {exchangeSnapshot.customer?.phone ? ` · ${exchangeSnapshot.customer.phone}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Original invoice: {exchangeSnapshot.original_invoice_number || '-'}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>Returned / old items</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Serial</TableCell>
                    <TableCell>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(exchangeSnapshot.old_items || []).length === 0 && (
                    <TableRow><TableCell colSpan={3}>-</TableCell></TableRow>
                  )}
                  {(exchangeSnapshot.old_items || []).map((it, i) => (
                    <TableRow key={i}>
                      <TableCell>{it.product_name}</TableCell>
                      <TableCell>{it.serial_number || '-'}</TableCell>
                      <TableCell>{it.unit_price || it.line_total || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" gutterBottom>New exchange unit</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Serial</TableCell>
                    <TableCell>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(exchangeSnapshot.new_items || []).map((it, i) => (
                    <TableRow key={i}>
                      <TableCell>{it.name}</TableCell>
                      <TableCell>{it.serial}</TableCell>
                      <TableCell>{it.price || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<PrintIcon />}
            onClick={() => {
              const el = document.getElementById('exchange-invoice-print');
              if (!el) return;
              const w = window.open('', '_blank', 'width=720,height=900');
              if (!w) return;
              w.document.write(`<html><head><title>Exchange Invoice</title>
                <style>body{font-family:Arial,sans-serif;padding:24px} table{width:100%;border-collapse:collapse}
                td,th{border:1px solid #ccc;padding:6px;text-align:left}</style></head><body>${el.innerHTML}</body></html>`);
              w.document.close();
              w.focus();
              w.print();
            }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const next = exchangeSnapshot?._navigate;
              setExchangePrintOpen(false);
              if (next) navigate(next);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceRequestForm;
